import PropTypes from 'prop-types';
import React, { Children, useCallback, useMemo, memo } from 'react';
import { List, Map, fromJS } from 'immutable';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import DeepValue from '../../decorators/DeepValue';
import ChangeDetection from '../../decorators/ChangeDetection';
import uniqueId from '../../../utils/uniqueId';

import style from './style.module.css';

/**
 * Dropdown wrapper.
 *
 * @param props {Object}.
 * @param props.id {string}.
 * @param props.buttonContent {string|React.Component} Content of dropdown button. Caret is added by
 *   this element. Need not pass it.
 * @param [props.wrapperClassName] {string} Class names for main div. `"dropdown"` class
 *   is already added to an element. Need not pass it.
 * @param [props.align=`"left"`] {string} Can be `"left"` or `"right"`. If align is left,
 *   all dropdown elements will start from the left side of the button.
 *   If align is right - from right part.
 * @param [props.className] {string} `<button>` class name. `"btn dropdown-toggle"` classes
 *    are already added to the element. Need not pass them.
 * @param [props.value=""] {string} Current value of dropdown. Hidden input has this value.
 * @param [props.name] {string} Name attribute for input.
 * @param [props.options] {Immutable.List} DropDown's content in js format.
 * @param [props.className] {String} Extra classNames for button tag of DD.
 * @param [props.onChange] {Function} OnDropDown change callback (need change value manual).
 * @param [props.isValuePicker=false] {boolean} Button shows the picked value, so it uses the form control font.
 */
const DropDown = ({
  id: idProp,
  name,
  disabled = false,
  changed = false,
  children,
  options = List(),
  align = 'left',
  isValuePicker = false,
  onChange,
  wrapperClassName: wrapperClassNameProp,
  className: classNameProp,
  ...restProps
}) => {
  const id = useMemo(() => idProp || uniqueId(), [idProp]);

  const computedOptions = useMemo(() => {
    let collectedOptions = List();

    Children.forEach(children, child => {
      if (!child) return;

      collectedOptions = collectedOptions.push(fromJS(child.props));
    });
    options.forEach(p => {
      collectedOptions = collectedOptions.push(p);
    });

    return collectedOptions;
  }, [children, options]);

  const mapOptionToChildren = useCallback(
    (option, i) => {
      const key = -1 - i;

      return <DropDownElement {...option.toJS()} key={key} name={name} />;
    },
    [name],
  );

  const handleClick = useCallback(
    event => {
      if (!onChange) return;
      if (!event.target) return;

      const parent = event.currentTarget;
      let currentElement = event.target;
      let currentParent = event.target.parentNode;

      while (currentParent !== parent && currentParent) {
        [currentElement, currentParent] = [currentParent, currentParent.parentNode];
      }

      let index = -1;

      if (currentParent === parent) {
        for (let i = 0; i < parent.childNodes.length; i++) {
          if (parent.childNodes[i] === currentElement) {
            index = i;
            break;
          }
        }
      }

      if (index === -1) return;

      const value = computedOptions.getIn([index, 'value']);

      if (value === undefined) return;
      event.target.name = name;
      event.target.value = value;

      return onChange(event);
    },
    [onChange, name, computedOptions],
  );

  const ulClassName = classNames('dropdown-menu', {
    'dropdown-menu-right': align === 'right',
  });
  const optionsContent = options.map(mapOptionToChildren);

  const content = [];

  Children.forEach(children, child => content.push(child));
  optionsContent.forEach(child => content.push(child));

  const wrapperClassName = classNames('dropdown', wrapperClassNameProp);
  const className = classNames('btn dropdown-toggle', classNameProp, {
    changed,
    [style.valuePicker]: isValuePicker,
  });

  const values = getValues(restProps);

  return (
    <div className={wrapperClassName}>
      <input name={name} type="hidden" value={values.value} />
      <button
        aria-expanded="true"
        aria-haspopup="true"
        className={className}
        data-toggle="dropdown"
        disabled={disabled}
        id={id}
        type="button"
      >
        {values.buttonContent} <span className="caret" />
      </button>
      <ul aria-labelledby={id} className={ulClassName} onClick={handleClick}>
        {content}
      </ul>
    </div>
  );
};

DropDown.propTypes = {
  align: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  isValuePicker: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.instanceOf(List),
  wrapperClassName: PropTypes.string,
};

/**
 * DropDown element row. Wraps content by `<li><a> ... </a></li>`.
 *
 * @param props {Object} Attributes for `<a>` tag in an element.
 * @param props.value {String} Value of this dropdown element.
 * @param [props.type="anchor"] {String} One of "anchor" or "link". If "anchor" - use `<a/>`
 *    If "link" - use ReactRouter's Link element.
 * For example `{ onClick:()=>{} }`.
 */
const DropDownElement = props => {
  const { value, to = '#', children = value, type = 'anchor', disabled = false, ...rest } = props;
  const { className: appendClassName, isDanger = false, ...properties } = rest;
  const className = classNames('min-height', appendClassName, { [style.danger]: isDanger });

  if (disabled) {
    properties.onClick = event => event.preventDefault();
    properties['aria-disabled'] = true;
  }

  let content;

  switch (type) {
    case 'link':
      content = (
        <Link className={className} to={to} {...properties}>
          {children}
        </Link>
      );
      break;

    case 'anchor':
      content = (
        <a className={className} href={to} {...properties}>
          {children}
        </a>
      );
      break;

    default:
      break;
  }

  return <li className={classNames({ disabled })}>{content}</li>;
};

DropDownElement.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  isDanger: PropTypes.bool,
  option: PropTypes.instanceOf(Map),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Map)]),
};

export { DropDownElement };

export default DeepValue(ChangeDetection(memo(DropDown)));

function getValues(props) {
  const result = {};

  if ('defaultValue' in props) {
    result.defaultValue = props.defaultValue;
  }

  if ('buttonContent' in props) {
    result.buttonContent = props.buttonContent;
    result.value = props.buttonContent;
  }

  if ('value' in props) {
    result.value = props.value;
  }

  if (Map.isMap(result.defaultValue)) {
    result.buttonContent = result.defaultValue.get('name');
    result.value = result.defaultValue.get('value');
  }

  if (Map.isMap(result.value)) {
    result.buttonContent = result.value.get('name');
    result.value = result.value.get('value');
  }

  return result;
}
